import type {
  AddMemberGroupMemberRequest,
  AddMemberGroupMemberResponse,
  CreateMemberGroupRequest,
  MemberGroup,
  MemberGroupMembersRequest,
  MemberGroupMembersResponse,
  MemberGroupModifierType,
  MemberGroupRequest,
  MemberGroupResponse,
  MemberGroupsOfMemberRequest,
  MemberGroupsRequest,
  MemberGroupsResponse,
  PaymentSettingModifier,
  PaymentSettingModifierData,
  RemoveMemberGroupMemberRequest,
  RemoveMemberGroupMemberResponse,
  UnformattedMemberGroup,
  UpdateMemberGroupRequest,
  UploadMemberGroupsCSVResponse
} from '@library/types/memberGroups';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { dropEmptyParams } from '@bo-utils/common';
import { defaultHeaders as headers } from '@bo-constants/apiHeaders';

const { memberGroup: baseUrl } = apiPaths;

function transformMemberGroup(
  memberGroup: UnformattedMemberGroup
): MemberGroup {
  const modifierId = memberGroup.relationships?.modifiers?.data.find(
    (modifier) => modifier.type === 'payment_setting_modifier'
  )?.id;
  return {
    ...memberGroup.attributes,
    id: Number(memberGroup.id),
    payment_setting_modifier_id:
      modifierId === undefined ? undefined : Number(modifierId)
  };
}

export const memberGroupsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMemberGroups: build.query<MemberGroup[], MemberGroupsRequest>({
      query: ({ name, description }) => ({
        url: baseUrl,
        method: 'GET',
        params: dropEmptyParams({ name, description }),
        headers
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'MemberGroups' as const,
                id
              })),
              'MemberGroups'
            ]
          : ['MemberGroups'],
      transformResponse: (response: MemberGroupsResponse) =>
        response.data.map((memberGroup) => transformMemberGroup(memberGroup))
    }),
    getMemberGroup: build.query<MemberGroup, MemberGroupRequest>({
      query: ({ id }) => ({
        url: `${baseUrl}/${id}`,
        method: 'GET',
        headers
      }),
      providesTags: (result) => [{ type: 'MemberGroups', id: result?.id }],
      transformResponse: ({ data }: MemberGroupResponse) =>
        transformMemberGroup(data)
    }),
    createMemberGroup: build.mutation<
      MemberGroupsResponse,
      CreateMemberGroupRequest
    >({
      query: (payload) => ({
        url: baseUrl,
        method: 'POST',
        body: payload,
        headers
      }),
      invalidatesTags: ['MemberGroups']
    }),
    updateMemberGroup: build.mutation<MemberGroup, UpdateMemberGroupRequest>({
      query: ({ id, ...payload }) => ({
        url: `${baseUrl}/${id}`,
        method: 'PUT',
        body: dropEmptyParams(payload),
        headers
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'MemberGroups', id: arg.id }
      ]
    }),
    getMemberGroupMembers: build.query<
      MemberGroupMembersResponse,
      MemberGroupMembersRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${baseUrl}/${id}/users`,
        method: 'GET',
        params: dropEmptyParams(params),
        headers
      }),
      providesTags: ['MemberGroupMembers']
    }),
    addMemberGroupMember: build.mutation<
      AddMemberGroupMemberResponse,
      AddMemberGroupMemberRequest
    >({
      query: ({ id, user_id }) => ({
        url: `${baseUrl}/${id}/users/${user_id}`,
        method: 'POST',
        headers
      }),
      invalidatesTags: ['MemberGroupMembers', 'Member']
    }),
    removeMemberGroupMember: build.mutation<
      RemoveMemberGroupMemberResponse,
      RemoveMemberGroupMemberRequest
    >({
      query: ({ id, user_id }) => ({
        url: `${baseUrl}/${id}/users/${user_id}`,
        method: 'DELETE',
        headers
      }),
      invalidatesTags: ['MemberGroupMembers', 'Member']
    }),
    getMemberGroupsOfMember: build.query<
      MemberGroup[],
      MemberGroupsOfMemberRequest
    >({
      query: ({ id }) => ({
        url: `${apiPaths.member}/${id}/user_groups`,
        method: 'GET',
        headers
      }),
      transformResponse: (response: MemberGroupsResponse) =>
        response.data.map((memberGroup) => ({
          ...memberGroup.attributes,
          id: Number(memberGroup.id)
        })),
      providesTags: ['MemberGroups', 'MemberGroupMembers']
    }),
    uploadMemberGroupsByCSV: build.mutation<
      UploadMemberGroupsCSVResponse,
      FormData
    >({
      query: (csv) => ({
        url: `${baseUrl}/upload_user_group_updates`,
        method: 'POST',
        body: csv,
        headers: {
          Accept: 'multipart/form-data'
        }
      }),
      invalidatesTags: ['MemberGroupMembers'],
      transformErrorResponse: (response) => response.data
    }),
    getPaymentSettingModifier: build.query<
      PaymentSettingModifier,
      { memberGroupId: MemberGroup['id'] }
    >({
      query: ({ memberGroupId }) => ({
        url: `${baseUrl}/${memberGroupId}/modifiers/${'PaymentSettingModifier' satisfies MemberGroupModifierType}`,
        method: 'GET',
        headers
      }),
      transformResponse: (response: { data: PaymentSettingModifierData }) =>
        response.data.attributes,
      providesTags: (result) => [
        { type: 'MemberGroupPaymentSettingModifiers', id: result?.id }
      ]
    }),
    getAllPaymentSettingModifiers: build.query<PaymentSettingModifier[], void>({
      query: () => ({
        url: `${baseUrl}/modifiers/${'PaymentSettingModifier' satisfies MemberGroupModifierType}`,
        method: 'GET',
        headers
      }),
      transformResponse: (response: { data: PaymentSettingModifierData[] }) =>
        response.data.map(({ attributes }) => attributes),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'MemberGroupPaymentSettingModifiers' as const,
                id
              })),
              'MemberGroupPaymentSettingModifiers'
            ]
          : ['MemberGroupPaymentSettingModifiers']
    }),
    updatePaymentSettingModifier: build.mutation<
      PaymentSettingModifier,
      { memberGroupId: MemberGroup['id'] } & Omit<
        PaymentSettingModifier,
        'id' | 'created_at' | 'updated_at'
      >
    >({
      query: ({ memberGroupId, ...body }) => ({
        url: `${baseUrl}/${memberGroupId}/modifiers/${'PaymentSettingModifier' satisfies MemberGroupModifierType}`,
        method: 'PATCH',
        body,
        headers
      }),
      transformResponse: (response: { data: PaymentSettingModifierData }) =>
        response.data.attributes,
      invalidatesTags: (result, _error, args) => [
        { type: 'MemberGroupPaymentSettingModifiers', id: result?.id },
        { type: 'MemberGroups', id: args.memberGroupId }
      ]
    }),
    deletePaymentSettingModifier: build.mutation<
      PaymentSettingModifier,
      { memberGroupId: MemberGroup['id'] }
    >({
      query: ({ memberGroupId }) => ({
        url: `${baseUrl}/${memberGroupId}/modifiers/${'PaymentSettingModifier' satisfies MemberGroupModifierType}`,
        method: 'DELETE',
        headers
      }),
      invalidatesTags: (result, _error, args) => [
        { type: 'MemberGroupPaymentSettingModifiers', id: result?.id },
        { type: 'MemberGroups', id: args.memberGroupId }
      ]
    })
  })
});

export const {
  useGetMemberGroupsQuery,
  useGetMemberGroupQuery,
  useCreateMemberGroupMutation,
  useUpdateMemberGroupMutation,
  useGetMemberGroupMembersQuery,
  useAddMemberGroupMemberMutation,
  useRemoveMemberGroupMemberMutation,
  useGetMemberGroupsOfMemberQuery,
  useUploadMemberGroupsByCSVMutation,
  useGetPaymentSettingModifierQuery,
  useGetAllPaymentSettingModifiersQuery,
  useUpdatePaymentSettingModifierMutation,
  useDeletePaymentSettingModifierMutation
} = memberGroupsApi;
